import { Toggle } from "@/components/ui/toggle";
import { useCourses } from "@/lib/courses";
import { useNotes } from "../contexts/NotesContext";
import { useCachedData } from "@/lib/sync";
import { useCommunication } from "@/lib/CommunicationWrapper";
import { formatDate } from "date-fns";
import { AudioLines, AudioWaveform, Folder, Plus, Search, SquarePen, X, FileText, ChevronRight, Clock, Sparkles, ChevronDown, Loader2, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";

const formatDateSafe = (date: string) => {
	try {
		return formatDate(date, "MMM d, yyyy");
	} catch {
		return "";
	}
}

import { useWidget } from "../lib/function-signatures";


const NoteItem = ({ note, active, index }: { note: any, active: boolean, index: number }) => {
	const { switchNote, deleteNoteById } = useNotes();
	const hasTranscript = note.has_transcript;

	const handleDelete = (e: React.MouseEvent) => {
		e.stopPropagation();
		deleteNoteById(note.id);
	};

	return (
		<motion.button
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{
				delay: index * 0.05,
				duration: 0.15,
				ease: "linear"
			}}
			className={`group cursor-pointer w-full flex items-center gap-3 p-3 rounded-xl transition-all duration-300 hover:bg-theme-1 border border-theme hover:shadow-sm"
			}`}
			onClick={() => switchNote(note.id)}
		>
			{/* Icon */}
			<div className={`shrink-0 size-10 rounded-lg flex items-center justify-center transition-all duration-300 ${active
				? "bg-blue-500/20 text-blue-600 scale-110"
				: "bg-theme-1 text-theme-1 group-hover:bg-theme-0/50 group-hover:text-theme-0"
				}`}>
				<FileText className="size-5" />
			</div>

			{/* Content */}
			<div className="flex-1 min-w-0 text-left">
				<div className="flex items-center gap-2 mb-1">
					<p className={`font-semibold text-sm truncate transition-colors duration-300 ${active ? "text-theme-0" : "text-theme-0 group-hover:text-theme-0"
						}`}>
						{note.title || "Untitled"}
					</p>
					{hasTranscript && (
						<div className="shrink-0 size-4 rounded-full bg-purple-500/20 flex items-center justify-center">
							<Sparkles className="size-2.5 text-purple-500" />
						</div>
					)}
				</div>
				<div className="flex items-center gap-1.5 text-xs text-theme-1">
					<Clock className="size-3" />
					<span>{formatDateSafe(note.modified_at, "MMM d, h:mm a")}</span>
				</div>
			</div>

			{/* Delete button - shows on hover */}
			<div
				onClick={handleDelete}
				className="shrink-0 size-8 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-red-500/20 cursor-pointer"
			>
				<Trash2 className="size-4 text-theme-1/50 hover:text-red-500 transition-colors" />
			</div>

			{/* Arrow indicator */}
			<ChevronRight className={`shrink-0 size-4 transition-all duration-300 ${active
				? "text-blue-500 translate-x-1"
				: "text-theme-1/50 group-hover:text-theme-0 group-hover:translate-x-1"
				}`} />
		</motion.button>
	)
}

export const CoursesNotesBrowser = () => {

	const { isWidget, activeCourse, notesMetadata, noteId, handleNewNote, switchNote, noteCount, noteLimit, canCreateNote, getNotesMetadata } = useNotes();
	const { authenticatedClient } = useCommunication();

	const [isSearchOpen, setIsSearchOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [searchResults, setSearchResults] = useState<any[]>([]);
	const [isLoadingMore, setIsLoadingMore] = useState(false);

	useEffect(() => {
		if (isSearchOpen) {
			setSearchQuery("");
		}
	}, [isSearchOpen]);

	useEffect(() => {

		if (searchQuery === "" || searchQuery.length < 2) {
			setSearchResults([]);
			return;
		}

		const fetchSearchResults = async () => {
			const client = await authenticatedClient();

			if (!client) {
				console.error("Error creating auth client:");
				return;
			}


			const { data, error, count } = await client
				.from('notes')
				.select('title, modified_at, id')
				.ilike('title', `%${searchQuery}%`)
				.order('modified_at', { ascending: false });

			console.log(data);

			setSearchResults(data || []);
		}
		fetchSearchResults();
	}, [searchQuery]);

	const openSearchResultNote = (id: number) => {
		switchNote(id.toString());
		setSearchResults([]);
		setIsSearchOpen(false);
		setSearchQuery("");
	}

	const renderSearchResults = () => {
		return searchResults.map((result) => (
			<button key={result.id} className="flex items-center justify-between gap-[10px] w-full" onClick={() => openSearchResultNote(result.id)}>
				<p className="text-sm font-medium">{result.title}</p>
				<p className="text-sm text-gray-500">{formatDateSafe(result.modified_at, "MMM d, yyyy")}</p>
			</button>
		))
	}

	const getTimeAgo = (date: string) => {
		const today = new Date();
		const pastDate = new Date(date);

		const msPerDay = 1000 * 60 * 60 * 24;

		// Use UTC to avoid timezone and DST issues
		const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
		const pastDateUtc = Date.UTC(pastDate.getFullYear(), pastDate.getMonth(), pastDate.getDate());

		return Math.floor((todayUtc - pastDateUtc) / msPerDay);
	}

	const loadMoreNotes = async () => {
		setIsLoadingMore(true);
		await getNotesMetadata(true, 6);
		setIsLoadingMore(false);
	};

	const renderNotes = () => {
		if (!notesMetadata[activeCourse]) return null;

		const sections = notesMetadata[activeCourse].reduce((acc: any, note: any) => {
			const date = getTimeAgo(note.modified_at);

			if (date <= 0) {
				acc.Today.push(note);
			} else if (date <= 1) {
				acc.Yesterday.push(note);
			} else if (date <= 7) {
				acc.Week.push(note);
			} else if (date <= 30) {
				acc.Month.push(note);
			} else {
				acc.Older.push(note);
			}
			return acc;
		}, { Today: [], Yesterday: [], Week: [], Month: [], Older: [] });

		// Check if all sections are empty
		const allSectionsEmpty = Object.values(sections).every(section =>
			!Array.isArray(section) || section.length === 0
		);

		// Render "no notes" message if all sections are empty
		if (allSectionsEmpty) {
			return (
				<div className="flex flex-col items-center justify-center py-12 text-center">
					<div className="size-16 rounded-2xl bg-theme-1 flex items-center justify-center mb-4">
						<FileText className="size-8 text-theme-1" />
					</div>
					<p className="text-sm font-medium text-theme-1 mb-1">No notes yet</p>
					<p className="text-xs text-theme-1/60">Create your first note to get started</p>
				</div>
			);
		}

		let globalIndex = 0;

		return Object.keys(sections).map((key: string) => {
			if (sections[key].length === 0) return null;

			return (
				<div className="flex flex-col gap-2 mb-6" key={key}>
					<div className="flex items-center gap-2 px-2 mb-1">
						<h3 className="text-xs font-bold uppercase tracking-wider text-theme-1/70">{key}</h3>
						<div className="flex-1 h-px bg-theme-1/20" />
						<span className="text-xs font-medium text-theme-1/50">{sections[key].length}</span>
					</div>
					<div className="flex flex-col gap-1.5">
						{sections[key].map((note: any) => {
							const itemIndex = globalIndex++;
							return <NoteItem key={note.id} note={note} active={note.id === noteId} index={itemIndex} />
						})}
					</div>
				</div>
			)
		});
	}

	return (
		<>
			<div className="flex items-center justify-between w-full gap-[10px] px-[10px] border-b border-theme h-[60px]">
				<div className="flex items-center flex-1 border border-theme bg-theme-1 rounded-md px-3 py-2 gap-[10px]">
					<Search className="w-[20px] h-[20px] text-theme-1 flex-shrink-0" />
					<input type="text" placeholder="Search" className="w-full border-0 outline-none text-sm" onChange={(e) => setSearchQuery(e.target.value)} value={searchQuery} />
				</div>

				<div className="flex items-center gap-2">
					<Toggle onClick={handleNewNote} pressed={false} className={!canCreateNote ? 'opacity-50' : ''}>
						<SquarePen className="min-w-[18px] min-h-[18px] text-theme-1 hover:text-theme-1" />
					</Toggle>
					{/*isWidget && <WidgetExitButton />}*/}
				</div>
			</div>
			<div className="flex-1 min-h-0 flex flex-col gap-[10px] overflow-y-auto p-[10px]">
				{searchQuery === "" ? renderNotes() : renderSearchResults()}

				{/* Load More Button at Bottom */}
				{searchQuery === "" && notesMetadata[activeCourse] && notesMetadata[activeCourse].length % 6 === 0 && notesMetadata[activeCourse].length > 0 && (
					<button
						onClick={loadMoreNotes}
						disabled={isLoadingMore}
						className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-theme-1 hover:bg-theme-0/50 text-theme-1 hover:text-theme-0 transition-all duration-200 border border-theme mb-4 disabled:opacity-50"
					>
						{isLoadingMore ? (
							<>
								<Loader2 className="size-4 animate-spin" />
								<span className="text-sm font-medium">Loading...</span>
							</>
						) : (
							<>
								<ChevronDown className="size-4" />
								<span className="text-sm font-medium">Load 6 more notes</span>
							</>
						)}
					</button>
				)}
			</div>
			{noteLimit > 0 && (
				<div className="border-t border-theme p-3">
					<div className="flex items-center justify-between text-sm mb-2">
						<span className="text-theme-1 font-medium">Notes Usage</span>
						<span className="text-theme-1">{noteCount} / {noteLimit}</span>
					</div>
					<div className="w-full h-2 bg-theme-1 border border-theme rounded-full overflow-hidden">
						<div
							className="h-full bg-theme-0 transition-all duration-300 rounded-full"
							style={{ width: `${Math.min((noteCount / noteLimit) * 100, 100)}%` }}
						/>
					</div>
				</div>
			)}
		</>
	)
}

/*
const WidgetExitButton = () => {

	const { closeWidget } = useWidget();
	return (
		<button onClick={() => closeWidget("stickyNote")} className="cursor-pointer hover:scale-110 transition-all duration-200">
			<X className="size-4" />
		</button>
	)
}
*/

export const CoursesNotesBrowserNew = () => {

	const { activeCourse, notesMetadata, noteId, handleNewNote, switchNote, noteCounts, noteLimit, canCreateNote, getNotesMetadata } = useNotes();
	const { authenticatedClient } = useCommunication();

	const [isLoadingMore, setIsLoadingMore] = useState(false);

	const [isSearchOpen, setIsSearchOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [searchResults, setSearchResults] = useState<any[]>([]);
	//const { data } = useCachedData("note_counts");

	const { courseData } = useCourses();

	useEffect(() => {
		if (isSearchOpen) {
			setSearchQuery("");
		}
	}, [isSearchOpen]);

	useEffect(() => {

		if (searchQuery === "" || searchQuery.length < 2) {
			setSearchResults([]);
			return;
		}

		const fetchSearchResults = async () => {
			const client = await authenticatedClient();

			if (!client) {
				console.error("Error creating auth client:");
				return;
			}


			const { data, error, count } = await client
				.from('notes')
				.select('title, modified_at, id')
				.ilike('title', `%${searchQuery}%`)
				.order('modified_at', { ascending: false });

			setSearchResults(data || []);
		}
		fetchSearchResults();
	}, [searchQuery]);

	const openSearchResultNote = (id: number) => {
		switchNote(id.toString());
		setSearchResults([]);
		setIsSearchOpen(false);
		setSearchQuery("");
	}

	const renderSearchResults = () => {
		return searchResults.map((result) => (
			<button key={result.id} className="flex items-center justify-between gap-[10px] w-full" onClick={() => openSearchResultNote(result.id)}>
				<p className="text-sm font-medium">{result.title}</p>
				<p className="text-sm text-gray-500">{formatDateSafe(result.modified_at, "MMM d, yyyy")}</p>
			</button>
		))
	}

	const getTimeAgo = (date: string) => {
		const today = new Date();
		const pastDate = new Date(date);

		const msPerDay = 1000 * 60 * 60 * 24;

		// Use UTC to avoid timezone and DST issues
		const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
		const pastDateUtc = Date.UTC(pastDate.getFullYear(), pastDate.getMonth(), pastDate.getDate());

		return Math.floor((todayUtc - pastDateUtc) / msPerDay);
	}

	const loadMoreNotes = async () => {
		setIsLoadingMore(true);
		await getNotesMetadata(true, 6);
		setIsLoadingMore(false);
	};

	const renderNotes = () => {
		if (!notesMetadata[activeCourse]) return null;

		const sections = notesMetadata[activeCourse].reduce((acc: any, note: any) => {
			const date = getTimeAgo(note.modified_at);

			if (date <= 0) {
				acc.Today.push(note);
			} else if (date <= 1) {
				acc.Yesterday.push(note);
			} else if (date <= 7) {
				acc.Week.push(note);
			} else if (date <= 30) {
				acc.Month.push(note);
			} else {
				acc.Older.push(note);
			}
			return acc;
		}, { Today: [], Yesterday: [], Week: [], Month: [], Older: [] });

		// Check if all sections are empty
		const allSectionsEmpty = Object.values(sections).every(section =>
			!Array.isArray(section) || section.length === 0
		);

		// Render "no notes" message if all sections are empty
		if (allSectionsEmpty) {
			return (
				<div className="flex flex-col items-center justify-center py-12 text-center">
					<div className="size-16 rounded-2xl bg-theme-1 flex items-center justify-center mb-4">
						<FileText className="size-8 text-theme-1" />
					</div>
					<p className="text-sm font-medium text-theme-1 mb-1">No notes yet</p>
					<p className="text-xs text-theme-1/60">Create your first note to get started</p>
				</div>
			);
		}

		let globalIndex = 0;

		return Object.keys(sections).map((key: string) => {
			if (sections[key].length === 0) return null;

			return (
				<div className="flex flex-col gap-2 mb-6" key={key}>
					<div className="flex items-center gap-2 px-2 mb-1">
						<h3 className="text-xs font-bold uppercase tracking-wider text-theme-1/70">{key}</h3>
						<div className="flex-1 h-px bg-theme-1/20" />
						<span className="text-xs font-medium text-theme-1/50">{sections[key].length}</span>
					</div>
					<div className="flex flex-col gap-1.5">
						{sections[key].map((note: any) => {
							const itemIndex = globalIndex++;
							return <NoteItem key={note.id} note={note} active={note.id === noteId} index={itemIndex} />
						})}
					</div>
				</div>
			)
		});
	}

	const noteCount = noteCounts[activeCourse] || 0;
	const courseImage = courseData?.[activeCourse]?.img || "";
	const courseColor = courseData?.[activeCourse]?.color || "gray";
	const courseCode = activeCourse === "all" ? "All courses" : activeCourse === 1 ? "Non-class" : courseData?.[activeCourse]?.code || courseData?.[activeCourse]?.default || "Unknown Course";
	if (!activeCourse) {
		return (
			<div className="flex justify-center w-full h-full bg-theme-0 items-center">
				<Loader2 className="size-8 animate-spin text-theme-1" />
			</div>
		)
	}

	return (
		<div className="flex justify-center w-full h-full bg-theme-0">
			<div className="pt-20 pb-12 max-w-[640px] w-full px-4 flex-col flex">
				<div className="mb-8 relative overflow-hidden rounded-2xl" style={{ borderLeft: `8px solid ${courseColor}` }}>


					<div
						className="absolute inset-0 bg-cover bg-center blur-sm"
						style={courseImage !== ""
							? { backgroundImage: `url(${courseImage})` }
							: {}
						}
					/>

					<div
						className="absolute inset-0 opacity-70"
						style={{ background: `linear-gradient(${courseColor} 0%, ${courseColor} 100%)` }}
					/>

					<div className="relative">
						<div className="p-8">
							<div className="flex items-start justify-between gap-4">
								{/* Course Icon */}
								<div
									className="h-20 w-[110px] rounded-2xl shadow-2xl flex items-center justify-center backdrop-blur-sm bg-white/10 overflow-hidden flex-shrink-0 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-xl border border-white/30 shadow-lg transition-all duration-200 font-medium text-sm flex items-center gap-2 flex-shrink-0"
									style={courseImage !== ""
										? { backgroundImage: `url(${courseImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
										: {}
									}
								>
									{courseImage === "" && <Folder className="size-8 text-white drop-shadow-lg" />}
								</div>

								{/* Course Info */}
								<div className="flex-1 min-w-0">
									<div className="flex items-start gap-3 mb-2">
										<h1 className="text-2xl font-bold text-white drop-shadow-lg leading-tight">
											{courseCode}
										</h1>
									</div>
									<div className="flex items-center gap-4 text-sm">
										<span className="rounded-full drop-shadow-sm text-white/90">
											{`${noteCount || "0"} notes`}
										</span>
									</div>
								</div>

								{/* New Note Button - Only show when a specific course is selected */}
								{activeCourse !== "all" && (
									<div className="flex flex-col items-end gap-2">
										<button onClick={() => handleNewNote(false)} className="backdrop-blur-md bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-xl border border-white/30 shadow-lg transition-all duration-200 hover:scale-105 font-medium text-sm flex items-center gap-2 flex-shrink-0">
											<Plus className="size-4" />
											New note
										</button>
										<button onClick={() => handleNewNote(true)} className="backdrop-blur-md bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-xl border border-white/30 shadow-lg transition-all duration-200 hover:scale-105 font-medium text-sm flex items-center gap-2 flex-shrink-0">
											<AudioLines className="size-4" />
											Transcribe lecture
										</button>
									</div>
								)}

							</div>
						</div>
					</div>

				</div>

				{/* Notes List */}
				<div className="flex-1 min-h-0 flex flex-col gap-2 overflow-y-auto">
					{renderNotes()}
					{/* Load More Button at Bottom */}
					{searchQuery === "" && notesMetadata[activeCourse] && notesMetadata[activeCourse].length % 6 === 0 && notesMetadata[activeCourse].length > 0 && (
						<button
							onClick={loadMoreNotes}
							disabled={isLoadingMore}
							className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-theme-1 hover:bg-theme-0/50 text-theme-1 hover:text-theme-0 transition-all duration-200 border border-theme mb-4 disabled:opacity-50"
						>

							{isLoadingMore && <Loader2 className="size-4 animate-spin" />}
							<span className="text-sm font-medium">Load more</span>
						</button>
					)}
				</div>
			</div>
		</div>
	)


	return (
		<>
			<div className="flex items-center justify-between w-full gap-[10px] px-[10px] border-b border-theme h-[60px]">
				<div className="flex items-center flex-1 border border-theme bg-theme-1 rounded-md px-3 py-2 gap-[10px]">
					<Search className="w-[20px] h-[20px] text-theme-1 flex-shrink-0" />
					<input type="text" placeholder="Search" className="w-full border-0 outline-none text-sm" onChange={(e) => setSearchQuery(e.target.value)} value={searchQuery} />
				</div>

				<div className="flex items-center gap-2">
					<Toggle onClick={handleNewNote} pressed={false} className={!canCreateNote ? 'opacity-50' : ''}>
						<SquarePen className="min-w-[18px] min-h-[18px] text-theme-1 hover:text-theme-1" />
					</Toggle>
					{/*isWidget && <WidgetExitButton />}*/}
				</div>
			</div>
			<div className="flex-1 min-h-0 flex flex-col gap-[10px] overflow-y-auto p-[10px]">
				{searchQuery === "" ? renderNotes() : renderSearchResults()}

				{/* Load More Button at Bottom */}
				{searchQuery === "" && notesMetadata[activeCourse] && notesMetadata[activeCourse].length % 6 === 0 && notesMetadata[activeCourse].length > 0 && (
					<button
						onClick={loadMoreNotes}
						disabled={isLoadingMore}
						className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-theme-1 hover:bg-theme-0/50 text-theme-1 hover:text-theme-0 transition-all duration-200 border border-theme mb-4 disabled:opacity-50"
					>
						{isLoadingMore ? (
							<>
								<Loader2 className="size-4 animate-spin" />
								<span className="text-sm font-medium">Loading...</span>
							</>
						) : (
							<>
								<ChevronDown className="size-4" />
								<span className="text-sm font-medium">Load 6 more notes</span>
							</>
						)}
					</button>
				)}
			</div>
			{noteLimit > 0 && (
				<div className="border-t border-theme p-3">
					<div className="flex items-center justify-between text-sm mb-2">
						<span className="text-theme-1 font-medium">Notes Usage</span>
						<span className="text-theme-1">{noteCount} / {noteLimit}</span>
					</div>
					<div className="w-full h-2 bg-theme-1 border border-theme rounded-full overflow-hidden">
						<div
							className="h-full bg-theme-0 transition-all duration-300 rounded-full"
							style={{ width: `${Math.min((noteCount / noteLimit) * 100, 100)}%` }}
						/>
					</div>
				</div>
			)}
		</>
	)
}